import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell from "../../components/argus/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Radar, Trash2, Play, ShieldCheck, Copy, Download, Loader2 } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import SeverityChip from "../../components/argus/SeverityChip";
import FindingDrawer from "../../components/argus/FindingDrawer";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [scans, setScans] = useState([]);
  const [findings, setFindings] = useState([]);
  const [active, setActive] = useState(null);
  const [verifyMethod, setVerifyMethod] = useState("dns");
  const [tab, setTab] = useState("findings");
  const [starting, setStarting] = useState(false);

  const activeScan = useMemo(
    () => scans.find((s) => s.status === "queued" || s.status === "running"),
    [scans],
  );
  const isScanning = !!activeScan;

  const refresh = async () => {
    const [p, s, f] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get("/scans", { params: { project_id: id } }),
      api.get("/findings", { params: { project_id: id } }),
    ]);
    setProject(p.data);
    // Preserve optimistic placeholders that the server hasn't picked up yet.
    setScans((prev) => {
      const placeholders = prev.filter(
        (x) =>
          x._placeholder &&
          Date.now() - new Date(x.started_at).getTime() < 4000 &&  // self-expire after 4s
          !s.data.some((rs) => new Date(rs.started_at).getTime() >= new Date(x.started_at).getTime() - 200),
      );
      return [...placeholders, ...s.data];
    });
    // /findings now returns {items,total,limit,offset}; tolerate both shapes
    setFindings(Array.isArray(f.data) ? f.data : (f.data?.items || []));
  };

  useEffect(() => { refresh(); }, [id]);

  useEffect(() => {
    const running = scans.some((s) => s.status === "running" || s.status === "queued");
    if (!running) return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scans]);

  const startScan = async () => {
    if (isScanning || starting) return;
    setStarting(true);
    // Optimistic placeholder so the running UI appears IMMEDIATELY — even for
    // sub-second scanners (SCA) where the backend may complete before our 2s poll.
    const placeholder = {
      id: `pending-${Date.now()}`,
      scanner: project.type,
      status: "queued",
      progress: 0,
      findings_count: 0,
      started_at: new Date().toISOString(),
      _placeholder: true,
    };
    setScans((prev) => [placeholder, ...prev]);
    setTab("scans");
    try {
      await api.post(`/projects/${id}/scans`);
      toast.success("Scan queued. Watch progress on the Scans tab.");
      // Quick burst of refreshes to catch the running → completed transition
      // for very fast scanners.
      await refresh();
      setTimeout(() => refresh(), 400);
      setTimeout(() => refresh(), 1200);
    } catch (e) {
      // Drop the placeholder on failure
      setScans((prev) => prev.filter((s) => !s._placeholder));
      toast.error(e?.response?.data?.detail || "Couldn't start the scan.");
    } finally {
      setStarting(false);
    }
  };

  const verify = async () => {
    try {
      await api.post(`/projects/${id}/verify-domain`, { method: verifyMethod });
      toast.success("Domain verified.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed.");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this project and all its findings? This cannot be undone.")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted.");
    nav("/app/projects");
  };

  const backup = async () => {
    try {
      const token = localStorage.getItem("argus.token");
      const r = await fetch(`${API}/projects/${id}/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `argus-backup-${id.slice(0, 8)}.json`;
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
      toast.success("Backup downloaded.");
    } catch (e) {
      toast.error(e?.message || "Backup failed.");
    }
  };

  const downloadReport = () => {
    const token = localStorage.getItem("argus.token");
    const url = `${API}/reports/${id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u;
        a.download = `argus-${project?.name?.replace(/\s+/g, "-") || id}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  if (!project) return <AppShell title="Project"><div className="text-ink/50">Loading…</div></AppShell>;

  const needsVerify = project.type === "dast" && !project.domain_verified;

  return (
    <AppShell
      title={project.name}
      actions={
        <>
          <Button variant="outline" onClick={downloadReport} className="h-10" data-testid="project-download-report">
            <Download size={16} className="mr-2" /> Download My Report
          </Button>
          {isScanning ? (
            <Button
              onClick={() => setTab("scans")}
              className="bg-gold text-paper hover:bg-gold2 h-10 font-semibold"
              data-testid="project-view-scan"
              title={`Scan ${activeScan.status} — ${activeScan.progress || 0}%`}
            >
              <Loader2 size={16} className="mr-2 animate-spin" />
              {activeScan.status === "queued" ? "Queued…" : `Scanning · ${activeScan.progress || 0}%`}
            </Button>
          ) : (
            <Button
              onClick={startScan}
              disabled={needsVerify || starting}
              className="bg-gold text-paper hover:bg-gold2 h-10 font-semibold"
              data-testid="project-start-scan"
            >
              {starting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
              {starting ? "Starting…" : "Start Scan"}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-6" data-testid="project-detail">
        <div className="rounded-xl border border-line bg-panel p-5 flex flex-wrap gap-6 items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-gold font-semibold mb-1">{project.type}</div>
            <div className="font-mono text-sm text-ink/60">{project.target || "—"}</div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div><span className="text-ink/50">Risk:</span> <span className="font-mono">{(project.risk_score || 0).toFixed(1)}</span></div>
            <div className="flex flex-wrap gap-1.5">
              {["critical", "high", "medium", "low"].filter((k) => (project.open_findings_count || {})[k] > 0).map((k) => (
                <span key={k} className={`sev-chip sev-${k}`}>{project.open_findings_count[k]} {k}</span>
              ))}
            </div>
          </div>
        </div>

        {isScanning && (
          <button
            onClick={() => setTab("scans")}
            className="w-full text-left rounded-xl border border-gold/40 bg-gold/5 p-5 flex items-center gap-4 hover:bg-gold/10 transition-colors"
            data-testid="scan-progress-banner"
          >
            <Loader2 size={20} className="text-gold animate-spin shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-ink">
                  {activeScan.status === "queued" ? "Scan queued" : "Scan running"}
                </span>
                <span className="text-xs uppercase tracking-wider text-gold/80 font-semibold">{activeScan.scanner}</span>
                <span className="text-xs text-ink/55">· started {new Date(activeScan.started_at).toLocaleTimeString()}</span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-raised overflow-hidden">
                <div
                  className="h-full bg-gold transition-all"
                  style={{ width: `${Math.max(2, activeScan.progress || 0)}%` }}
                />
              </div>
              <div className="mt-1.5 text-xs text-ink/55">
                {activeScan.progress || 0}% · Click to view scan details →
              </div>
            </div>
          </button>
        )}

        {needsVerify && (
          <div className="rounded-xl border border-sev-medium/40 bg-sev-medium/5 p-5" data-testid="verify-banner">
            <div className="flex items-start gap-3">
              <ShieldCheck className="text-sev-medium mt-1" />
              <div className="flex-1">
                <h3 className="font-display text-xl">Verify domain ownership.</h3>
                <p className="text-sm text-ink/70 mt-1">DAST scans only run against domains you control. Pick a method:</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <label className={`rounded-lg border p-3 cursor-pointer ${verifyMethod === "dns" ? "border-ink bg-raised/40" : "border-line"}`}>
                    <input type="radio" name="m" className="hidden" checked={verifyMethod === "dns"} onChange={() => setVerifyMethod("dns")} />
                    <div className="font-semibold text-sm">DNS TXT record</div>
                    <div className="text-xs text-ink/60 mt-1 font-mono break-all">argus-verify={project.verification_token}</div>
                  </label>
                  <label className={`rounded-lg border p-3 cursor-pointer ${verifyMethod === "file" ? "border-ink bg-raised/40" : "border-line"}`}>
                    <input type="radio" name="m" className="hidden" checked={verifyMethod === "file"} onChange={() => setVerifyMethod("file")} />
                    <div className="font-semibold text-sm">HTTPS file</div>
                    <div className="text-xs text-ink/60 mt-1 font-mono break-all">/.well-known/argus-verify-{project.verification_token}.txt</div>
                  </label>
                </div>
                <div className="mt-4 flex gap-2 items-center">
                  <Button onClick={verify} className="bg-gold text-paper hover:bg-gold2 h-10" data-testid="verify-button">Verify Now</Button>
                  <button
                    className="text-xs text-ink/60 inline-flex items-center gap-1 hover:text-ink"
                    onClick={() => { navigator.clipboard.writeText(project.verification_token || ""); toast.success("Token copied."); }}
                    data-testid="verify-copy"
                  >
                    <Copy size={12} /> Copy token
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="project-tabs">
            <TabsTrigger value="findings" data-testid="tab-project-findings">Findings ({findings.length})</TabsTrigger>
            <TabsTrigger value="scans" data-testid="tab-project-scans">
              Scans ({scans.length}){isScanning ? <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-gold animate-pulse" /> : null}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-project-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="findings">
            {findings.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line p-12 text-center text-ink/60" data-testid="findings-empty">
                {isScanning ? (
                  <>Scan in progress — findings will appear here as they're detected. <button className="text-gold" onClick={() => setTab("scans")}>View scan progress →</button></>
                ) : (
                  <>No findings yet. That's either very good or we haven't scanned. <button className="text-gold" onClick={startScan} disabled={starting}>Run your first scan →</button></>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-line bg-panel overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2.5">Severity</th>
                      <th className="text-left px-4 py-2.5">Title</th>
                      <th className="text-left px-4 py-2.5 hidden md:table-cell">Asset</th>
                      <th className="text-left px-4 py-2.5 hidden md:table-cell">CWE</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((f) => (
                      <tr key={f.id} className="border-t border-line hover:bg-raised/40 cursor-pointer dense-row" onClick={() => setActive(f)} data-testid={`finding-row-${f.id}`}>
                        <td className="px-4"><SeverityChip severity={f.severity} /></td>
                        <td className="px-4 max-w-md truncate">{f.title}</td>
                        <td className="px-4 font-mono text-xs hidden md:table-cell truncate max-w-[180px]">{f.asset}</td>
                        <td className="px-4 font-mono text-xs hidden md:table-cell">{f.cwe_id}</td>
                        <td className="px-4 text-xs">{f.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="scans">
            {scans.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line p-12 text-center text-ink/60">
                No scans yet. <button className="text-gold" onClick={startScan}>Start one →</button>
              </div>
            ) : (
              <div className="rounded-xl border border-line bg-panel overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2.5">Scan</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                      <th className="text-left px-4 py-2.5">Findings</th>
                      <th className="text-left px-4 py-2.5">Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scans.map((s) => {
                      const live = s.status === "running" || s.status === "queued";
                      return (
                        <tr key={s.id} className={`border-t border-line dense-row ${live ? "bg-gold/5" : ""}`} data-testid={`scan-row-${s.id}`}>
                          <td className="px-4 font-mono text-xs">{s.id.slice(0, 8)}</td>
                          <td className="px-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              {live && <Loader2 size={12} className="animate-spin text-gold" />}
                              <span className={`text-xs ${s.status === "completed" ? "text-safe" : s.status === "running" ? "text-gold" : s.status === "failed" ? "text-sev-critical" : "text-ink/60"}`}>{s.status}</span>
                              {live && (
                                <>
                                  <div className="h-1.5 w-24 bg-raised rounded-full overflow-hidden"><div className="h-full bg-gold transition-all" style={{ width: `${Math.max(2, s.progress || 0)}%` }} /></div>
                                  <span className="font-mono text-xs text-ink/60">{s.progress || 0}%</span>
                                </>
                              )}
                            </div>
                            {s.error && <div className="text-xs text-sev-critical mt-1">{s.error}</div>}
                          </td>
                          <td className="px-4">{s.findings_count ?? 0}</td>
                          <td className="px-4 text-xs text-ink/60 font-mono">{new Date(s.started_at).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings">
            <div className="rounded-xl border border-line bg-panel p-6 max-w-xl space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/50">Project name</label>
                <Input defaultValue={project.name} className="h-12 mt-1" onBlur={(e) => api.patch(`/projects/${id}`, { name: e.target.value }).then(refresh)} data-testid="project-rename" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/50">Target</label>
                <Input defaultValue={project.target} className="h-12 mt-1 font-mono" onBlur={(e) => api.patch(`/projects/${id}`, { target: e.target.value }).then(refresh)} data-testid="project-target-edit" />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" onClick={backup} className="border-line bg-transparent" data-testid="project-backup">
                  <Download size={16} className="mr-2" /> Backup project (JSON)
                </Button>
                <Button variant="destructive" onClick={remove} data-testid="project-delete">
                  <Trash2 size={16} className="mr-2" /> Delete project
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <FindingDrawer
        finding={active}
        open={!!active}
        onOpenChange={(o) => { if (!o) setActive(null); }}
        onUpdated={refresh}
      />
    </AppShell>
  );
}
